// icons — the single source of truth for iconography (ADR-042).
//
// Icons are referenced by MEANING, never picked by glyph at a call site:
//
//     import { icons } from '../ui/icons.js';
//     <icons.edit size={16} aria-hidden />          // not <PencilSimple .../>
//
// WHY: glyph-per-call-site drifts — the same concept ends up rendered by different
// glyphs across features. Keying by concept makes the glyph decision ONCE, here, and
// freezes it. A lint gate (no-restricted-imports) bans direct `@phosphor-icons/react`
// imports everywhere EXCEPT this file, so a session reuses a concept instead of
// inventing a glyph.
//
// GROUPED SOURCE → FLAT LOOKUP. Concepts are authored in `GROUPS` (by family), and
// the flat `icons` lookup + the catalogue's `ICON_GROUPS` are BOTH derived from it.
// So: call sites stay `icons.user`; the `UI/Icons` story groups itself; and adding a
// concept to a group here shows up — correctly grouped — in the catalogue with NO
// story edit. (Add a whole new group? also add its spread to `icons` below.)
//
// CHOOSING AN ICON (the rule, also in ADR-042):
//   1. Need an icon? Find the concept in a group below and use `icons.<concept>`.
//   2. It's a STATE of an existing family? Add a sibling key named after the domain's
//      EXISTING status vocabulary (don't invent a parallel one). For a RANGED family
//      (battery / signal level) add a resolver fn in the CUSTOM section, not N keys.
//      Build state families on recurrence, not speculation.
//   3. Genuinely new concept? Add ONE line to the right group.
//   4. Two glyphs fit one meaning equally? STOP and ask the architect; record the
//      winner here so it is never re-litigated.
//
// SAME FAMILY, DIFFERENT MEANING → keep both, name them apart. The canonical pair:
//   `check` (bare tick = selection / "this one" / done-inline) vs
//   `statusSuccess` (CheckCircle = success as a standalone status).
//   Likewise `close` (X) vs `statusError` (XCircle).
//
// CONVENTIONS: color is `currentColor` (NEVER a hex/rgb prop — the parent token
// colors the glyph); size is per call site; decorative glyphs are `aria-hidden` (the
// control's text / `aria-label` is the accessible name); directional glyphs mirror in
// RTL (`rtl:-scale-x-100`).

import {
  // entities / domains
  House, Pulse, MapPin, Cube, Ticket, ChartBar, User, Users, UsersThree, ShieldCheck,
  Buildings, Globe, TreeStructure, MapTrifold, Broadcast, Package, Warehouse, Toolbox, Truck, Lightning, Gauge, TelegramLogo,
  Certificate, Robot, Network, SimCard,
  // actions
  Plus, PencilSimple, Trash, MagnifyingGlass, FunnelSimple, Export, DownloadSimple, UploadSimple,
  Copy, ArrowCounterClockwise, ArrowsClockwise, PlugsConnected, SignOut, X, DotsThreeVertical, DotsThree, DotsSixVertical, ArrowRight, List, Wrench,
  // status
  CheckCircle, Warning, XCircle, Info, PauseCircle, Prohibit, Archive,
  // the four observation KINDS (ADR-061 §5 — kind is drawn as a glyph, severity as colour+weight)
  CircleDashed, NotEquals, ClockCountdown, ShieldWarning,
  // indicators / form / disclosure
  Check, CaretDoubleDown, CaretDoubleUp, CaretDown, CaretLeft, CaretRight, CaretUp, CaretUpDown, Eye, EyeSlash, Star, Bell, Question,
  // settings / view
  Gear, Sliders, Stack, StackSimple, Cpu,
  // time
  CalendarBlank, Clock, ClockCounterClockwise,
  // security
  Key, LockSimple,
  // devices
  Desktop, DeviceMobile, DeviceTablet, Browser, TerminalWindow, FolderSimple, ArrowsLeftRight,
  // people
  UserSwitch, Binoculars,
  // files
  File, FileXls, FileCsv, FilePdf, FileDoc, FilePpt, FileTxt, FileZip, CompassTool, LinkSimple,
  // theme
  Sun, Moon,
  // energy sources (the solution-config mix — EnergyMix, monitoring cards)
  Plugs, Engine, BatteryHigh, Windmill, GasPump, Shield, Snowflake, Circuitry, Leaf, Handshake, Briefcase,
  // asset-category kit (the register's identity glyph per category)
  SolarPanel, SolarRoof, PlugCharging, WaveSine, Thermometer, Cylinder, HardDrives, Drop, BatteryCharging,
  CarBattery, BatteryVerticalHigh, Lockers,
  // data / misc
  Tag, ListNumbers, ClipboardText, Scroll, Code, Flag, Tray, Receipt, Shapes,
  // contract vocabulary (the Terms role trio + bands + the config entity)
  TrendUp, SealCheck, BracketsSquare, SquaresFour, SelectionSlash,
  Folder,
  Image,
  Play,
  // the files library (ADR-068 — the media family, the stage, the panel's reserved sections)
  Images, VideoCamera, Microphone, Camera, CornersOut, CornersIn, MagnifyingGlassPlus, MagnifyingGlassMinus,
  ChatCircle, Sparkle,
} from '@phosphor-icons/react';

/** A Phosphor icon component type (re-exported so consumers never import the lib). */
export type { Icon } from '@phosphor-icons/react';

/**
 * The catalogue, authored by family. Reference concepts via the flat `icons` lookup
 * below (`icons.user`); this grouped shape only drives organisation + the `UI/Icons`
 * story. Add a concept to the right group (and the story stays in sync).
 */
const GROUPS = {
  Entities: {
    home: House,
    monitoring: Pulse,
    site: MapPin,
    asset: Cube,
    // Asset-category glyphs (the register peek's leading identity; energy-source
    // concepts below already cover battery / solar / generator / cooling / wind /
    // fueling — these three fill the register's remaining kinds).
    module: StackSimple, // a rack module (a plug-in unit in a shelf — the rectifier)
    controller: Cpu, // a controller unit (the power-system brain)
    gateway: Broadcast, // a comms gateway (the connectivity box)
    rack: HardDrives, // a cabinet / rack enclosure (the thing modules plug into)
    ticket: Ticket,
    report: ChartBar,
    invoicing: Receipt, // the invoicing / billing MODULE identity (nav rows, empty states) — the Terms role trio has its own glyphs (Data group)
    books: Receipt, // the asset BOOKS facet (the money record — edit door rows, the books face); shares the glyph, different surface
    assetOwner: Briefcase, // an asset OWNER — the legal entity a unit's books point at (our company · customer · lessor); nav row, empty state, audit rows
    contractualConfig: SquaresFour, // a contractual configuration — a cell of the pricing matrix (entity kind; peek header)
    samePricing: StackSimple, // several configs stacked in ONE pricing cell — the same-pricing (candidate-collision) signal
    user: User, // a single person / "my account"
    users: Users, // the people collection (admin Users, an empty users list)
    team: UsersThree,
    observer: Binoculars, // a team member who follows the work without being assigned it — "can be assigned" off (ADR-083 §2); the exception wears the glyph, the default nothing
    role: ShieldCheck, // a permission set (RBAC) — same glyph in nav + the role chip
    license: Certificate, // a license tier / the licensing catalogue (ADR-055)
    building: Buildings,
    administrativeArea: Globe,
    cluster: TreeStructure,
    zone: MapTrifold,
    siteType: Broadcast,
    package: Package,
    warehouse: Warehouse, // a warehouse/depot (distinct from `package`, a parcel/box)
    siteStorage: Toolbox, // a site's OWN storeroom/spares stash (the third storage kind — T ruling: location kinds read as icons; distinct from `warehouse` = a standalone building, `package` = a parcel, `statusArchived` = the retired chip)
    devices: Network, // the Devices surface — a managed device ON THE NETWORK (the NMS convention; T ruling — Circuitry read as electronics, and it already means `powerSystem`)
    cellular: SimCard, // the cellular REACH facts of a device (its SIMs — the SMS control number + the fallback address; ADR-063's `cellular` kind)
    // The service DOORS (the Devices entry's Services cell rail; per-kind
    // dial doors, distinct from `devices` = the surface and `gateway` = the box):
    servicePolling: ArrowsLeftRight, // the polling door (SNMP/Modbus — what collectors dial); the Services cell's compact-face glyph
    serviceWeb: Browser, // the web management door (web-ui; http-api rides it)
    serviceSsh: TerminalWindow, // the SSH door
    serviceFtp: FolderSimple, // the FTP door (config/log pulls)
    vehicle: Truck, // service vehicle / fleet
    energy: Lightning,
    gauge: Gauge,
    telegram: TelegramLogo,
    integration: PlugsConnected, // an external service wired to the platform (provider-neutral — never a vendor logo)
    apiClient: Robot, // an application API credential (non-human caller — bot, data producer, partner system; distinct from `password`/Key = human SSO)
  },
  Actions: {
    add: Plus,
    edit: PencilSimple,
    delete: Trash,
    search: MagnifyingGlass,
    filter: FunnelSimple,
    export: Export,
    download: DownloadSimple,
    upload: UploadSimple,
    play: Play, // media playback (a video tile's affordance)
    copy: Copy,
    restore: ArrowCounterClockwise, // reactivate / undo
    handover: UserSwitch, // a person's responsibility chains given to successors (ADR-081 §5)
    recheck: ArrowsClockwise, // re-run a check / refresh a live status (cf. `restore`, which undoes)
    signOut: SignOut,
    close: X, // close / dismiss / clear (cf. statusError for the circled error glyph)
    more: DotsThreeVertical, // overflow menu (row / vertical context)
    moreHorizontal: DotsThree, // inline overflow (horizontal context)
    drag: DotsSixVertical, // drag handle (reorder — SortableList)
    forward: ArrowRight,
    menu: List, // hamburger / nav toggle
    maintenance: Wrench,
  },
  Status: {
    statusSuccess: CheckCircle, // success state (cf. `check` for a bare selection tick)
    statusWarning: Warning,
    statusError: XCircle,
    statusInfo: Info,
    statusPaused: PauseCircle,
    statusBlocked: Prohibit,
    statusArchived: Archive,
    // ── The four observation KINDS (ADR-061 §2/§5). One glyph each, the same four on
    //    every register in the product — the whole learning cost of the system.
    //    Colour and weight carry SEVERITY and are applied by the caller; these
    //    glyphs never encode how bad something is.
    observationMissing: CircleDashed, // an outline with nothing in it — we don't know something
    observationConflict: NotEquals, // two records that don't agree
    observationUnverified: ClockCountdown, // nobody has checked recently (distinct from `clock`, a plain time)
    observationBreach: ShieldWarning, // a commitment is not met
  },
  Indicators: {
    check: Check, // selection / affirmative / done-inline (NOT a status — see statusSuccess)
    caretDown: CaretDown,
    caretDoubleDown: CaretDoubleDown, // every band open (the gear's row)
    caretDoubleUp: CaretDoubleUp, // every band folded
    caretLeft: CaretLeft,
    caretRight: CaretRight,
    caretUp: CaretUp,
    sort: CaretUpDown,
    reveal: Eye,
    hide: EyeSlash,
    favorite: Star, // follow — the subscription star (sites, tickets; never the panel pin)
    notifications: Bell,
    help: Question, // inline help affordance (per-key / per-section explainer)
  },
  'Settings & view': {
    settings: Gear, // the one settings glyph (GearSix retired into this)
    adjust: Sliders,
    columns: Stack, // column-visibility / view shaping
  },
  Time: {
    calendar: CalendarBlank,
    clock: Clock,
    history: ClockCounterClockwise, // audit / activity log
  },
  Security: {
    password: Key,
    token: Key, // a personal access token (human programmatic credential — ADR-004; glyph shared with `password`, concept distinct)
    locked: LockSimple, // an immutable / closed-off record (an invoiced-period lock)
  },
  Devices: {
    deviceDesktop: Desktop,
    deviceMobile: DeviceMobile,
    deviceTablet: DeviceTablet,
  },
  Files: {
    file: File,
    fileExcel: FileXls,
    fileCsv: FileCsv,
    // The document library's TYPED glyphs (FRONTEND-PATTERNS §Files) — recognition by
    // shape (Phosphor's letters), monochrome: never a decorative per-type hue.
    filePdf: FilePdf,
    fileWord: FileDoc,
    filePowerpoint: FilePpt,
    fileText: FileTxt, // plain text (a CSV is `fileCsv`)
    fileArchive: FileZip,
    fileCad: CompassTool, // a drawing file (DWG · DXF) — the drafting compass
  link: LinkSimple, // copy link · a link to a place in the app
    folder: Folder,
    image: Image,
    media: Images, // the described family as ONE word — photos · videos · voice notes (the flow, the filter group, the phone's door)
    video: VideoCamera,
    audio: Microphone, // a voice note
    camera: Camera, // the phone's capture door ("Add media")
    expand: CornersOut, // a preview to full screen (the stage)
    collapse: CornersIn,
    zoomIn: MagnifyingGlassPlus,
    zoomOut: MagnifyingGlassMinus,
    comments: ChatCircle, // the panel's reserved Comments section
    assistant: Sparkle, // the panel's reserved Ask section (the assistant integration)
  },
  Theme: {
    themeLight: Sun,
    themeDark: Moon,
  },
  // The energy-SOURCE mix of a site's solution config (EnergyMix strip, the
  // solution section, monitoring cards). Presence glyphs — live/ranged STATE
  // (generator running, battery level) is a monitoring-slice resolver in the
  // custom section, never sibling keys here. `fueling` (GasPump) is the diesel
  // LOGISTICS concept (fuel runs, tank levels) — distinct from sourceGenerator,
  // the DG as an energy source.
  Energy: {
    sourceGrid: Plugs,
    sourceSolar: Sun, // solar = the Sun (glyph lock — clearer than a PV panel at strip size)
    sourceGenerator: Engine, // the ON-SITE generator (own DG + tank) — never the provider genset
    sourceProviderGen: Shield, // provider generator — a shared genset backing the site (ADR-056 §6; + a 24/7 tag when auto-failover)
    sourceBattery: BatteryHigh,
    powerSystem: Circuitry, // the power system + battery as ONE kit (the contract's core cluster)
    sustainability: Leaf, // carbon & renewables — the environmental term family
    sourceWind: Windmill, // a wind turbine reads truer than the breeze glyph
    sourceCooling: Snowflake, // cooling — a site LOAD in the solution config (ADR-056 §5), never a supply glyph in the SITES strip; the contract composition glyph DOES show it (a priced axis there)
    fueling: GasPump,
    gridBackup: Shield, // grid backup / resilience — reinforces the grid glyph
    // The register's per-category kit — PHYSICAL UNITS, distinct from the
    // energy-SOURCE presence glyphs above (a site "has solar" = sourceSolar/Sun;
    // an asset that IS an array of panels = solarPanel).
    solarPanel: SolarPanel, // ONE panel — the string's unit (serial-bearing, theft-tracked)
    solarString: SolarRoof, // the STRING/array of panels (distinct silhouettes — the battery pair's ruling)
    ups: BatteryCharging, // a UPS unit (battery + charge path — distinct from the battery pair's glyphs)
    charger: PlugCharging, // a solar charge controller (MPPT) — it charges, it isn't the source
    inverter: WaveSine, // an inverter module — the SINE it makes (architect: Waves read as water)
    thermostat: Thermometer, // a cooling controller (lead-lag / thermostat) — distinct from sourceCooling (the load)
    fuelTank: Cylinder, // the fuel tank as a VESSEL silhouette (architect: GasCan read as a jerrycan) — distinct from `fueling` (GasPump, the logistics act)
    fuelLevel: Drop, // a fuel-level probe / sensor — it measures liquid
    batteryString: CarBattery, // the BANK (a string of modules in series) — deliberately not the cell glyph
    batteryModule: BatteryVerticalHigh, // ONE module/cell — the string's unit (distinct silhouettes, architect-ruled)
    cabinet: Lockers, // an outdoor cabinet / shelter enclosure — doors, not drive shelves (rack stays for true racks)
    // Rectifier stays on `module` (StackSimple) for now — the honest AC→DC
    // glyph has no Phosphor equivalent and is the first ADR-042 §6 custom-SVG
    // candidate (sine→line through a diode), architect-flagged.
  },
  Data: {
    tag: Tag,
    priorities: ListNumbers,
    categories: Shapes, // site categories — the 4th priced-designation axis (ADR-060 §3)
    siteFields: Tag, // the folded "Site fields" surface — the four site vocabularies (types · priorities · categories · tags) as tabs; a label glyph (shared with `tag`, like matchKey/password — concepts stay distinct)

    matchKey: Key, // an import dataset's row-identity column (glyph shared with `password` — concepts stay distinct)
    responsibilities: ClipboardText,
    contract: Scroll,
    terms: Handshake, // the contract-terms reference surface (terms · bands · flags)
    // The Terms ROLE pair (drivers · commitments) — one glyph per role,
    // shared by the Terms groups, the configs list rollups, and the config peek.
    pricingDriver: TrendUp, // a priced axis — the term moves the invoice
    commitment: SealCheck, // a contractually binding obligation (the seal on the promise)
    bands: BracketsSquare, // a band/tier — one interval of a partitioned axis
    notIncluded: SelectionSlash, // absent by choice — "not in this contract" / not part of the selection
    code: Code,
    flag: Flag,
    tray: Tray, // neutral "nothing here" — the first-run empty-state default
  },
} as const;

/**
 * Concept → glyph. The flat lookup every call site uses (`icons.edit`). Derived from
 * `GROUPS` — adding a concept to a group above is enough; only a brand-new GROUP needs
 * its spread added here too.
 */
export const icons = {
  ...GROUPS.Entities,
  ...GROUPS.Actions,
  ...GROUPS.Status,
  ...GROUPS.Indicators,
  ...GROUPS['Settings & view'],
  ...GROUPS.Time,
  ...GROUPS.Security,
  ...GROUPS.Devices,
  ...GROUPS.Files,
  ...GROUPS.Theme,
  ...GROUPS.Energy,
  ...GROUPS.Data,
} as const;

/** `[{ label, keys }]` per family — drives the `UI/Icons` catalogue story. */
export const ICON_GROUPS: { label: string; keys: (keyof typeof icons)[] }[] = Object.entries(
  GROUPS,
).map(([label, members]) => ({ label, keys: Object.keys(members) as (keyof typeof icons)[] }));

// ── CUSTOM / domain glyphs ──────────────────────────────────────────────
// For concepts with NO Phosphor equivalent. (The energy-SOURCE set anticipated
// here turned out to exist upstream — Plugs / Sun / Engine / BatteryHigh /
// Windmill shipped in Phosphor and live in `GROUPS.Energy` above; verify the installed
// set before hand-authoring.) A genuinely missing glyph lands here as a
// hand-authored SVG component reading `currentColor` (NEVER hardcoded hex, the
// mistake in dev's retired DomainIcons), Phosphor-styled (24×24, 1.5 stroke),
// then joins its GROUPS family so the catalogue stays grouped automatically.
//
// STATUS-LEVEL families (ranged domain state — battery, signal, fuel) get a RESOLVER,
// not N loose keys, so one place owns the thresholds and the level→glyph map:
//
//   export type BatteryLevel = 'full' | 'high' | 'medium' | 'low' | 'empty' | 'charging';
//   export function batteryIcon(level: BatteryLevel): Icon { /* maps level → custom glyph */ }
//   // call site:  const Bat = batteryIcon(level);  <Bat size={16} aria-hidden />
//
// (none yet — build on recurrence, at the first energy UI.)
